export function isDateInRange(value, fromDate, toDate) {
  if (!fromDate && !toDate) return true;
  if (!value) return false;

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return false;

  if (fromDate) {
    const from = new Date(fromDate);
    from.setHours(0, 0, 0, 0);
    if (date < from) return false;
  }

  if (toDate) {
    const to = new Date(toDate);
    to.setHours(23, 59, 59, 999);
    if (date > to) return false;
  }

  return true;
}

export function matchesUploadedDateRange(uploadedDate, fromDate, toDate) {
  return isDateInRange(uploadedDate, fromDate, toDate);
}

export function sortByUploadedDateDesc(items, getUploadedDate = (item) => item?.uploadedDate) {
  return [...items].sort(
    (a, b) => new Date(getUploadedDate(b) || 0) - new Date(getUploadedDate(a) || 0)
  );
}

export function todayDateInputValue(referenceDate = new Date()) {
  const today = new Date(referenceDate);
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, '0');
  const day = String(today.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

export function getMonthStartDateInputValue(referenceDate = new Date()) {
  const date = new Date(referenceDate);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  return `${year}-${month}-01`;
}

export function getEndOfMonthDateInputValue(referenceDate = new Date()) {
  const date = new Date(referenceDate);
  const year = date.getFullYear();
  const month = date.getMonth() + 1;
  const lastDay = new Date(year, month, 0).getDate();
  const monthStr = String(month).padStart(2, '0');
  const dayStr = String(lastDay).padStart(2, '0');
  return `${year}-${monthStr}-${dayStr}`;
}

export function getDateInputValueDaysAgo(daysAgo, referenceDate = new Date()) {
  const date = new Date(referenceDate);
  date.setDate(date.getDate() - daysAgo);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

export function getDefaultDashboardDateRange(referenceDate = new Date()) {
  return {
    from: getMonthStartDateInputValue(referenceDate),
    to: todayDateInputValue(referenceDate),
  };
}

/** First day through last day of the calendar month before referenceDate. */
export function getPreviousMonthDateRange(referenceDate = new Date()) {
  const anchor = new Date(referenceDate);
  anchor.setDate(1);
  anchor.setMonth(anchor.getMonth() - 1);
  return {
    from: getMonthStartDateInputValue(anchor),
    to: getEndOfMonthDateInputValue(anchor),
  };
}

/** Requirement List default: last 60 days including today. */
export function getDefaultRequirementListMonthRange(referenceDate = new Date()) {
  return {
    from: getDateInputValueDaysAgo(59, referenceDate),
    to: todayDateInputValue(referenceDate),
  };
}

export function matchesDefaultDashboardDateRange(fromDate, toDate, referenceDate = new Date()) {
  const expected = getDefaultDashboardDateRange(referenceDate);
  return fromDate === expected.from && toDate === expected.to;
}

export function matchesDefaultRequirementListMonthRange(fromDate, toDate, referenceDate = new Date()) {
  const expected = getDefaultRequirementListMonthRange(referenceDate);
  return fromDate === expected.from && toDate === expected.to;
}

/** Month-to-date default: extend interview filter through month end so upcoming interviews in the month are visible. */
export function resolveDashboardInterviewDateRange(dateRange = {}, referenceDate = new Date()) {
  const { from = '', to = '' } = dateRange;
  if (!from || !to) return { from, to };

  const today = todayDateInputValue(referenceDate);
  if (to !== today) return { from, to };

  const fromMonth = from.slice(0, 7);
  const toMonth = to.slice(0, 7);
  if (fromMonth !== toMonth) return { from, to };

  const endOfMonth = getEndOfMonthDateInputValue(referenceDate);
  if (to >= endOfMonth) return { from, to };

  return { from, to: endOfMonth };
}
